import { AfterViewInit, Component, DestroyRef, EventEmitter, inject, OnInit, signal, TemplateRef, viewChild } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { AdminService } from '../../services/admin.service';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { catchError, combineLatest, debounceTime, EMPTY, forkJoin, map, merge, of, startWith, switchMap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatTableDataSource } from '@angular/material/table';
import { ScheduledTask } from '../../../models/scheduled-task.model';
import { FormBuilder, Validators } from '@angular/forms';
import { ScheduledTasksService } from '../../services/scheduled-tasks.service';
import moment from 'moment';
import { StatusesService } from '../../services/statuses.service';
import { Status } from '../../../models/status.model';
import { Requirement } from '../../../models/requirement.model';
import { RequirementsService } from '../../../services/requirements.service';
import { WorksService } from '../../services/works.service';
import { LocationService } from '../../services/location.service';
import { Location } from '../../../models/location.model';

const JOB_CLASS = 'DocumentsBulkMovementWorker';

@Component({
    selector: 'app-admin-order-massive-movement',
    templateUrl: './admin-order-massive-movement.component.html',
    styleUrls: ['./admin-order-massive-movement.component.scss'],
    standalone: false
})
export class AdminOrderMassiveMovementComponent implements OnInit, AfterViewInit {

  public reload = new EventEmitter<void>();


  public currentTime = moment().add(5, 'minute');

  private readonly fb = inject(FormBuilder);

  dataSource = new MatTableDataSource<ScheduledTask>();

  displayedColumns: string[] = ['statuses_origin', 'status_target', 'run_at', 'rows_affected', 'status', 'actions'];

  public filters = this.fb.group({
    'status_ids[]': this.fb.control<string>(null, {nonNullable: true}),
  });


  private readonly adminService = inject(AdminService);


  public form = this.fb.group({
    run_at: this.fb.control(this.currentTime, {nonNullable: true, validators: [Validators.required]}),
    run_now: this.fb.control<boolean>(false, { nonNullable: true }),
    job_class: this.fb.control<string>(JOB_CLASS, {nonNullable: true, validators: [Validators.required]}),
    payload: this.fb.group({
      locations_ids: this.fb.control<string>(null),
      requirements_ids: this.fb.control<string>(null),
      works_ids: this.fb.control<string>(null),
      origin_status_ids: this.fb.control<string>(null, Validators.required),
      target_status_id: this.fb.control<string>(null, Validators.required),
      threshold_days: this.fb.control<number>(7, {nonNullable: true, validators: [Validators.required]}),
      type: this.fb.control<string>('Order', {nonNullable: true, validators: [Validators.required]}),
      company_id: this.fb.control<number>(this.adminService.local$.getValue().id, {nonNullable: true, validators: [Validators.required]}),
    })
  });

  public isLoading = signal<boolean>(true);

  public locations = signal<Location[]>([]);

  private matDialogRef: MatDialogRef<any> | null = null;
  
  public pageSizes = [10, 25, 50, 100];

  private paginator = viewChild<MatPaginator>('paginator');

  public requirements = signal<Requirement[]>(undefined);

  public saving = signal<boolean>(false);

  public sort = 'run_at desc';

  private sorter = viewChild<MatSort>(MatSort);

  public statuses = signal<Status[]>([]);

  public totalData = 0;

  public totalPages = 0;

  public works = signal<any[]>([]);

  private readonly destroyRef = inject(DestroyRef);

  private readonly locationService = inject(LocationService);

  private readonly matDialog = inject(MatDialog);

  private readonly requirementService = inject(RequirementsService);

  private readonly scheduledTasksService = inject(ScheduledTasksService);

  private readonly snackBar = inject(MatSnackBar);

  private readonly statusService = inject(StatusesService);

  private readonly workService = inject(WorksService);


  FormTemplate = viewChild<TemplateRef<any>>('formTemplate');
  

  ngOnInit(): void {
    this.dataSource.paginator = this.paginator();

    combineLatest({
      locations: this.locationService.all(),
      requirements: this.requirementService.requirements$,
      works: this.workService.all(`Work::Order`),
      statuses: this.statusService.statuses('Status::Order'),
    }).subscribe((response) => {
      this.locations.set(response.locations.body as Location[]);
      this.requirements.set(response.requirements);
      this.works.set(response.works.body as any[]);
      this.statuses.set(response.statuses);
    });

    this.addFormHandlersAndValidators();
  }

  ngAfterViewInit(): void {
    this.adminService.pageTitle.set(this.adminService.getText('financial_movement_plural', 'capitalize'));

    this.dataSource.paginator = this.paginator();

    merge(this.paginator().page, this.filters.valueChanges, this.reload, this.sorter().sortChange.asObservable())
    .pipe(
      takeUntilDestroyed(this.destroyRef),
      debounceTime(300),
      startWith(null),
      switchMap(() => {
        this.isLoading.set(true);
        return this.getTableData$(
          (this.paginator()?.pageIndex ?? 0) + 1,
          (this.paginator()?.pageSize ?? 10),
        ).pipe(catchError(() => of(null)));
      }),
      map((data) => {
        this.isLoading.set(false);
        if (data == null) return [];

        this.totalData = data.body.total_count;
        this.totalPages = data.body.total_pages;

        return data.body.items;
      }),
    )
    .subscribe((data) => {
      this.dataSource = new MatTableDataSource(data);
    });
  }

  private getTableData$(pageIndex: number, pageSize: number) {
    return this.scheduledTasksService.index(
      JOB_CLASS,
      pageIndex,
      pageSize,
      this.filters.value,
      this.sort
    );
  }

  onSortChange(event: any) {
    if (!event?.direction) {
      this.sort = 'run_at desc';
      return;
    }

    this.sort = `${event.active} ${event.direction}`;
  }

  openForm() {
    this.matDialogRef = this.matDialog.open(this.FormTemplate(), { width: '80%' });

    this.matDialogRef.afterClosed().subscribe(() => {
      this.form.reset();
    })
  }

  displayOriginStatuses(task: ScheduledTask): string {
    return this.statuses().filter((status) => {
      return task.payload.origin_status_ids.includes(status.id);
    }).map((status) => status.name).join(', ');
  }

  displayTargetStatus(task: ScheduledTask): string {
    return this.statuses().find((status) => {
      return task.payload.target_status_id === status.id;
    })?.name;
  }

  get taskStatuses() {
    return {
      'pending': {name: 'Pendiente'},
      'cancelled': { name: 'Cancelado' },
      'failed': { name: 'Fallido' },
      'running': { name: 'Ejecutándose' },
      'completed': { name: 'Exitoso' },
    }
  }

  get taskStatusesKeys() {
    return Object.keys(this.taskStatuses);
  }

  mapStatus(status: string) {
    return this.taskStatuses[status] ?? status;
  }

  save() {
    const data = this.form.value;

    this.scheduledTasksService.create(data)
      .pipe(
        catchError(() => {
          this.saving.set(false);
          this.snackBar.open('Error al ingresar tarea programada', 'Cerrar', {
            duration: 3000,
            panelClass: 'snackbar-error',
          });
          return EMPTY;
        }),
      )
    .subscribe(() => {
      this.saving.set(false);
      this.snackBar.open('Tarea programada agendada', 'Cerrar', {
        duration: 3000,
        panelClass: 'snackbar-success',
      });

      this.reload.emit();

      this.form.reset();
      this.matDialogRef.close();
    })
  }

  cancel(task_id: number) {
    this.scheduledTasksService.cancel(task_id)
      .pipe(
        catchError(() => {
          this.saving.set(false);
          this.snackBar.open('Error al cancelar tarea programada', 'Cerrar', {
            duration: 3000,
            panelClass: 'snackbar-error',
          });
          return EMPTY;
        }),
      )
    .subscribe(() => {
      this.saving.set(false);
      this.snackBar.open('Tarea programada cancelada', 'Cerrar', {
        duration: 3000,
        panelClass: 'snackbar-success',
      });

      this.reload.emit();
    })
  }

  runNow(task_id: number) {
    this.scheduledTasksService.runNow(task_id)
      .pipe(
        catchError(() => {
          this.saving.set(false);
          this.snackBar.open('Error al ejecutar tarea programada', 'Cerrar', {
            duration: 3000,
            panelClass: 'snackbar-error',
          });
          return EMPTY;
        }),
      )
    .subscribe(() => {
      this.saving.set(false);
      this.snackBar.open('Tarea programada ejecutada', 'Cerrar', {
        duration: 3000,
        panelClass: 'snackbar-success',
      });

      this.reload.emit();
    })    
  }

  private addFormHandlersAndValidators() {
    this.form.get('run_now')?.valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((executeNow) => {
      if (executeNow) {
        this.form.get('run_at')?.clearValidators()
        this.form.get('run_at')?.updateValueAndValidity();
      } else {
        this.form.get('run_at')?.setValidators([Validators.required]);
        this.form.get('run_at')?.updateValueAndValidity();
        this.form.get('run_at')?.setValue(moment().add(5, 'minute'));
      }
    });
  }
}
